import { create } from "zustand";
import { socket } from "../components/mediasoup/socket";
import { isHostRole } from "../utils/role.utils";

/* ---------------- Types ---------------- */
export type StatusMap = Record<string, boolean>;

interface JudgeCameraList {
  userId: string;
  label: string;
  deviceId: string;
}

interface UserData {
  _id: string;
  role: string;
  name: string;
  roomNo: string;
  roomId: string;
}

interface CourtRoomData {
  courtRoomId: string;
  courtRoomNo: string;
  courtRoomName: string;
  highcourt: string;
  xPoweredBy: string;
}

interface MeetingStore {
  /* ---------------- UI State ---------------- */

  causeListShow: boolean;
  setCauseListShow: (v: boolean) => void;

  viewerShow: boolean;
  setViewerShow: (v: boolean) => void;

  recording: string;
  setRecording: (v: string) => void;
  activeRecordingCnr: string | null;
  setActiveRecordingCnr: (v: string | null) => void;
  recordingStartedAt: number | null;
  setRecordingStartedAt: (v: number | null) => void;

  isStreaming: boolean;
  setIsStreaming: (v: boolean) => void;
  streamingStartedAt: number | null;
  setStreamingStartedAt: (v: number | null) => void;

  pathname: string;
  setPathname: (v: string) => void;

  readerCauseIconShow: boolean;
  setReaderCauseIconShow: (v: boolean) => void;

  meetingStarted: boolean;
  setMeetingStarted: (v: boolean) => void;

  mediaJoining: boolean;
  setMediaJoining: (v: boolean) => void;
  localMicOn: boolean;
  setLocalMicOn: (v: boolean) => void;
  localMicLocked: boolean;
  setLocalMicLocked: (v: boolean) => void;
  /* ---------------- User / Case ---------------- */

  userData: UserData;
  setUserData: (data: UserData) => void;

  courtRoomData: CourtRoomData;
  setCourtRoomData: (v: CourtRoomData) => void;

  causes: any;
  setCauses: (data: any | ((prev: any) => any)) => void;

  selectedCase: any;
  setSelectedCase: (data: any) => void;

  advocateCases: any;
  setAdvocateCases: (data: any) => void;
  advocteActiveCase: any;
  setAdvocateActiveCase: (data: any) => void;

  viewers: [];
  setViewers: (data: any) => void;

  /* ---------------- Camera Allocation ---------------- */

  cameraAssignments: Record<string, string>;
  assignCamera: (userId: string, deviceId: string) => void;

  /* ---------------- Mic Allocation ---------------- */

  micAssignments: Record<string, string>;
  assignMic: (userId: string, deviceId: string) => void;

  /* ---------------- Legacy ---------------- */

  judgeCameraSelected: JudgeCameraList;
  setJudgeCameraSelected: (data: JudgeCameraList) => void;

  /* ---------------- Test ---------------- */

  test: any;
  setTest: (data: any) => void;

  /* ---------------- Actions ---------------- */

  muted: StatusMap;
  setMuted: (data: StatusMap) => void;

  video: any;
  setVideo: (fn: (prev: StatusMap) => StatusMap) => void;

  blur: StatusMap;
  setBlur: (data: StatusMap) => void;

  freeze: any;
  setFreeze: (data: any) => void;

  muteAll: boolean;
  setMuteAll: (v: boolean) => void;

  isHost: () => boolean;

  cleanupJudgeMeeting: () => void;
  cleanupAdvocateMeetings: () => void;

  localStreams: MediaStream[];
  setLocalStreams: (streams: MediaStream[]) => void;
  stopLocalStreams: () => void;
}

/* ---------------- Store ---------------- */

export const useMeetingStore = create<MeetingStore>((set, get) => ({
  /* UI */
  causeListShow: true,
  setCauseListShow: (v) => set({ causeListShow: v }),

  viewerShow: false,
  setViewerShow: (v) => set({ viewerShow: v }),

  recording: "stop",
  setRecording: (v) => set({ recording: v }),
  activeRecordingCnr: null,
  setActiveRecordingCnr: (v) => set({ activeRecordingCnr: v }),
  recordingStartedAt: null,
  setRecordingStartedAt: (v) => set({ recordingStartedAt: v }),

  isStreaming: false,
  setIsStreaming: (v) => set({ isStreaming: v }),
  streamingStartedAt: null,
  setStreamingStartedAt: (v) => set({ streamingStartedAt: v }),

  pathname: "",
  setPathname: (v) => set({ pathname: v }),

  readerCauseIconShow: false,
  setReaderCauseIconShow: (v) => set({ readerCauseIconShow: v }),

  meetingStarted: false,
  setMeetingStarted: (v) => set({ meetingStarted: v }),

  mediaJoining: true,
  setMediaJoining: (v) => set({ mediaJoining: v }),
  localMicOn: false,
  setLocalMicOn: (v) => set({ localMicOn: v }),
  localMicLocked: false,
  setLocalMicLocked: (v) => set({ localMicLocked: v }),

  /* User / Case */
  userData: {
    _id: "",
    role: "",
    name: "",
    roomNo: "",
    roomId: "",
  },
  setUserData: (data) => set({ userData: data }),
  courtRoomData: {
    courtRoomNo: "",
    courtRoomId: "",
    courtRoomName: "",
    highcourt: "",
    xPoweredBy: "",
  },
  setCourtRoomData: (data) => set({ courtRoomData: data }),

  causes: [],
  setCauses: (data) =>
    set((state) => ({
      causes: typeof data === "function" ? data(state.causes) : data,
    })),

  selectedCase: null,
  setSelectedCase: (data) => set({ selectedCase: data }),

  advocateCases: {
    socket: null,
    cases: [],
  },
  setAdvocateCases: (data) => set({ advocateCases: data }),

  advocteActiveCase: null,
  setAdvocateActiveCase: (data) => set({ advocteActiveCase: data }),

  viewers: [],
  setViewers: (data) => set({ viewers: data }),

  muted: {},
  setMuted: (data) => set({ muted: data }),

  video: {},
  setVideo: (fn) =>
    set((state) => ({
      video: fn(state.video),
    })),

  blur: {},
  setBlur: (data) => set({ blur: data }),

  freeze: {},
  setFreeze: (data) => set({ freeze: data }),

  muteAll: false,
  setMuteAll: (data) => set({ muteAll: data }),

  isHost: () => {
    const role: any = get().userData?.role;
    return isHostRole(role);
  },

  /* Camera */
  cameraAssignments: {},
  assignCamera: (userId, deviceId) =>
    set((state) => ({
      cameraAssignments: {
        ...state.cameraAssignments,
        [userId]: deviceId,
      },
    })),

  /* Mic */
  micAssignments: {},
  assignMic: (userId, deviceId) =>
    set((state) => ({
      micAssignments: {
        ...state.micAssignments,
        [userId]: deviceId,
      },
    })),

  /* Legacy */
  judgeCameraSelected: {
    userId: "",
    label: "",
    deviceId: "",
  },
  setJudgeCameraSelected: (data) => set({ judgeCameraSelected: data }),

  /* Test */
  test: null,
  setTest: (data) => set({ test: data }),

  /* ---------------- Cleanup Logic ---------------- */

  cleanupJudgeMeeting: () => {
    const { userData, selectedCase, pathname } = get();
    if (pathname === "/judge-meeting") return;
    if (userData?.role !== "Judge") return;
    if (!selectedCase) return;

    socket?.emit("end-meeting", {
      roomId: userData.roomId,
      roomNo: userData.roomNo,
      actionBy: userData._id,
      cnr: selectedCase.cnr,
    });
  },

  cleanupAdvocateMeetings: () => {
    const { userData, advocateCases, pathname } = get();

    if (pathname === "/advocate-lobby" || pathname === "/meeting") return;
    if (!userData) return;

    advocateCases?.cases?.forEach((caseItem: any) => {
      advocateCases?.socket?.emit("leave-meeting", {
        meetingId: `${caseItem.courtRoom}_${caseItem.courtRoomId}`,
        roomNo: caseItem.courtRoom,
        roomId: caseItem.courtRoomId,
        userId: userData._id,
        role: userData.role,
        cnr: caseItem.cnr,
        reason: "USER_LEFT",
      });
    });
  },

  /* ---------------- Local Media Streams ---------------- */
  localStreams: [],
  setLocalStreams: (streams) => set({ localStreams: streams }),
  stopLocalStreams: () => {
    const { localStreams } = get();
    localStreams.forEach((stream) => {
      console.log("stream...", stream.getTracks);
      stream.getTracks().forEach((track) => {
        track.stop();
        console.log(
          `[stopLocalStreams] stopped ${track.kind} track: ${track.label}`,
        );
      });
    });
    set({ localStreams: [] });
  },
}));
